import React from "react";
import { Col, Label, Row } from "reactstrap";
import NumberField from "./NumberField";
import StringField from "./StringField";
import EntitySelectField from "./EntitySelectField";
import BooleanField from "./BooleanField";
import ServiceField from "./ServiceField";
import ArrayTextField from "./ArrayTextField";
import ArrayContentField from "./ArrayContentField";
import ArraySimpleField from "./ArraySimpleField";
import LinkField from "./LinkField";

/** The variable entry a block edits; hosts pass the template/page row as-is. */
export type SettingVar = {
  variable?: string;
  type?: string;
  complextype?: string;
  value?: unknown;
};

/**
 * A building block. `any` props on purpose: the registry and the host override
 * maps hold blocks with different prop shapes (each declares its own type), and
 * the dispatcher only forwards settingVar / options / onChange plus fieldProps.
 * `ownsChrome` marks a block that renders its own header (SCRUM-643).
 */
export type FieldComponent = React.ComponentType<any> & { ownsChrome?: boolean }; // any: blocks declare different prop shapes; the dispatcher forwards only the shared props

/** Host overrides per type key (`service`, `Array:text`, …); null renders nothing. */
export type FieldOverrides = Record<string, FieldComponent | null>;

/**
 * VariableField — dispatcher for the per-type variable building blocks
 * (SCRUM-551, step 3 of the SCRUM-549 VariableField refactor; design in
 * P2CMS/docs/Template_VariableFields_Refactor.md).
 *
 * Picks the building block from REGISTRY by `settingVar.type` and renders
 * the uniform label + (future) error message around it. Building blocks
 * know nothing about required/optional/create — they report the new value
 * via `onChange(name, value)`; the host screen decides where it goes
 * (saveSettingsRequired / saveSettingsOptional / setTemplate).
 *
 * The registry grows per migration step (step 4: EntitySelectField,
 * step 5: the rest). A type that is not in the registry returns null so the
 * host's legacy per-type blocks keep handling it (strangler migration).
 */
const REGISTRY: Record<string, FieldComponent> = {
  Number: NumberField,
  String: StringField,
  // the entity-reference picker; the options source is injected by the host
  // (SCRUM-552). Category / Subcategory / Ad / Blog shared it, and Json / Image
  // had blocks of their own, until SCRUM-2207: no template declares them.
  Product: EntitySelectField,
  // remaining building blocks (SCRUM-553); Array dispatches on complextype
  Boolean: BooleanField,
  service: ServiceField,
  Link: LinkField,
};

/** Array has three editors, selected by the variable's complextype. */
const ARRAY_REGISTRY: Record<string, FieldComponent> = {
  text: ArrayTextField,
  content: ArrayContentField,
};

function resolveField(settingVar: SettingVar): FieldComponent | undefined {
  if (settingVar.type === "Array") {
    return ARRAY_REGISTRY[settingVar.complextype ?? ""] ?? ArraySimpleField;
  }
  return REGISTRY[settingVar.type ?? ""];
}

/**
 * Override lookup (SCRUM-554, extended for the pages side in SCRUM-643).
 * Array variables check the specific `Array:<complextype>` key BEFORE the
 * plain `Array` key, so a host can reroute one complextype (e.g.
 * `Array:product` to a multi entity select) without taking over the
 * text/content routing that ARRAY_REGISTRY already handles.
 */
function resolveOverride(
  settingVar: SettingVar,
  overrides: FieldOverrides | undefined
): { Field: FieldComponent | null } | undefined {
  if (!overrides) return undefined;
  const keys =
    settingVar.type === "Array"
      ? [`Array:${settingVar.complextype}`, "Array"]
      : [settingVar.type ?? ""];
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(overrides, key)) {
      return { Field: overrides[key] };
    }
  }
  return undefined;
}

/** True when the registry can render this variable type. */
export function hasVariableField(type: string): boolean {
  return type === "Array" || Object.prototype.hasOwnProperty.call(REGISTRY, type);
}

/**
 * True when the dispatcher resolves a renderable block for this variable
 * under the given overrides — lets a host decide between <VariableField/>
 * and its own legacy fallback (the update screens' "Unknown type" warning)
 * without duplicating the resolution rules (SCRUM-643).
 */
export function hasFieldFor(settingVar: SettingVar, overrides?: FieldOverrides): boolean {
  const hit = resolveOverride(settingVar, overrides);
  if (hit) return hit.Field != null;
  return resolveField(settingVar) != null;
}

/**
 * Closure helper for host override maps: binds extra props on a block while
 * carrying over its `ownsChrome` marker, so a wrapped composite keeps
 * skipping the dispatcher's label row (SCRUM-643).
 */
export function bindFieldProps(Field: FieldComponent, bound: Record<string, unknown>): FieldComponent {
  const Bound: FieldComponent = (props: Record<string, unknown>) => <Field {...props} {...bound} />;
  Bound.ownsChrome = Field.ownsChrome;
  return Bound;
}

export type VariableFieldProps = {
  settingVar: SettingVar;
  options?: unknown;
  onChange?: (name: string, value: never) => void;
  /** per-field validation message (wired up in step 7) */
  error?: React.ReactNode;
  /** host-supplied per-type block overrides (SCRUM-554) */
  overrides?: FieldOverrides;
  /**
   * extra per-surface props spread onto the resolved block (SCRUM-643) — for
   * DYNAMIC per-item props (summaryProps drag handles, validationError,
   * variant, pages/categories slices) that would otherwise force per-render
   * closure overrides and remount the block subtree on every keystroke.
   * Blocks ignore props they don't declare.
   */
  fieldProps?: Record<string, unknown> & { summaryProps?: object | null };
};

export default function VariableField({
  settingVar,
  options,
  onChange,
  error = undefined,
  overrides = undefined,
  fieldProps = undefined,
}: VariableFieldProps) {
  // hosts may override a type's block (e.g. TemplateForm's create card:
  // service keeps its URL input — SCRUM-554)
  const overrideHit = resolveOverride(settingVar, overrides);
  const Field = overrideHit ? overrideHit.Field : resolveField(settingVar);
  if (!Field) return null; // unknown type: render nothing (same as today)

  // blocks that render their own header (the pages-side composites: a
  // <details>/<summary> with the variable name) skip the dispatcher's label
  // row — otherwise the variable name would appear twice (SCRUM-643)
  if (Field.ownsChrome) {
    return (
      <>
        <Field settingVar={settingVar} options={options} onChange={onChange} {...fieldProps} />
        {error && <div className="text-danger">{error}</div>}
      </>
    );
  }

  return (
    <div>
      <Row>
        {/* LABEL FOR VARIABLE NAME — fieldProps.summaryProps lands here for
            shell-rendered blocks (the wizard's label-as-drag-handle types),
            exactly like ownsChrome blocks spread it on their own header */}
        <Label
          className="form-control-label"
          htmlFor="example-text-input"
          md="3"
          {...(fieldProps && fieldProps.summaryProps)}
        >
          {settingVar.variable}
        </Label>
        {/* INPUT FOR VARIABLE VALUE */}
        <Col md="9">
          <Field settingVar={settingVar} options={options} onChange={onChange} {...fieldProps} />
        </Col>
      </Row>
      {/* one place for per-field validation feedback — wired up in step 7 (SCRUM-504) */}
      {error && <div className="text-danger">{error}</div>}
    </div>
  );
}
